import React, { useMemo, useState, useEffect } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import Markdown from 'react-markdown';

import {
  Form,
  FormItem,
  Input,
  SubmitButton,
  Loading,
  Container,
  SearchPanel,
  Button,
} from '../../../../../components';
import { SearchIcon } from '../../../../../components/SearchBar/SearchIcon';
import type { FormProps } from '../hooks/useKnowledgeSourceDetails';
import { useKnowledgeSourceDetails } from '../hooks/useKnowledgeSourceDetails';
import clsx from 'clsx';
import {
  validateLimitSize,
  validateThresholdSize,
} from '../../utils/validator';
import type { SearchResult } from '../../../../../api';

type SourceModalType = 'search' | 'view';

type Props = {
  type: SourceModalType;
  dataSourceId?: string;
  onEditChunk?: (chunk: ContentData) => void;
  refreshTrigger?: number;
  isModalOpen?: boolean;
};

export type ContentData = SearchResult & {
  document?: string;
  id?: string;
};

export const SourceDetailsModal: React.FC<Props> = ({
  type,
  dataSourceId,
  onEditChunk,
  refreshTrigger,
  isModalOpen,
}) => {
  const {
    setForm,
    form,
    handleSearchKnowledge,
    searchingKnowledge,
    searchResult,
    dataChunks,
    pagination,
    loadingChunks,
    setPageDetails,
    refresh,
    resetSearch,
  } = useKnowledgeSourceDetails(dataSourceId);

  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(
    null
  );
  const [selectedChunkId, setSelectedChunkId] = useState<string | null>(null);

  const isSeachModalType = useMemo(() => type === 'search', [type]);

  useEffect(() => {
    if (!isModalOpen && isSeachModalType) {
      resetSearch();
      setSelectedItemIndex(null);
      setSelectedChunkId(null);
    }
  }, [isModalOpen, isSeachModalType, resetSearch]);

  const handleEditClick = (item: ContentData, index: number) => {
    setSelectedItemIndex(index);
    setSelectedChunkId(item.id || null);
    onEditChunk?.(item);
  };

  const contentData = useMemo(() => {
    const data = isSeachModalType
      ? (searchResult?.results as ContentData[])
      : (dataChunks as ContentData[]);

    const mapped =
      data?.map(item => {
        const chunkId = (item as any).chunk_id || item.id;

        return {
          ...item,
          content: item?.document || item?.content,
          id: chunkId,
        };
      }) || [];

    return mapped;
  }, [isSeachModalType, searchResult?.results, dataChunks]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0 && isModalOpen) {
      if (isSeachModalType) {
        handleSearchKnowledge();
      } else {
        refresh();
      }
    }
  }, [
    refreshTrigger,
    isSeachModalType,
    refresh,
    handleSearchKnowledge,
    isModalOpen,
  ]);

  useEffect(() => {
    if (selectedChunkId && contentData.length > 0) {
      const newIndex = contentData.findIndex(
        item => item.id === selectedChunkId
      );
      if (newIndex !== -1) {
        setSelectedItemIndex(newIndex);
      }
    }
  }, [contentData, selectedChunkId]);

  const renderContentList = () => {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto max-h-[80vh]">
        <div className="flex text-secondary font-semibold text-medium gap-x-20 sticky top-0 z-10 border-b bg-white border-border pl-4">
          {isSeachModalType && (
            <div className="w-20 text-medium text-semibold text-secondary px-4 self-center">
              Score
            </div>
          )}
          <div className="text-medium text-semibold text-secondary px-4">
            Text
          </div>
        </div>
        {!contentData?.length ? (
          <div className="text-medium text-semibold text-secondary px-4">
            No Data
          </div>
        ) : (
          contentData.map((item, index) => (
            <div
              key={`${index}-${item.metadata.chunk_length}`}
              className={`flex justify-between items-center border-b border-border py-4 ${
                selectedItemIndex === index ? 'bg-select' : ''
              }`}
            >
              <div className="flex px-4 gap-x-2 flex-1 items-center">
                {item.similarity && (
                  <div className="text-mainText font-semibold text-small px-4 w-20 shrink-0">
                    {item.similarity?.toFixed(5)}
                  </div>
                )}
                <div className="shrink-0 px-2 ml-4">
                  <Button
                    size="small"
                    type="empty"
                    label=""
                    onClick={() => handleEditClick(item, index)}
                    className="px-1 py-2 border border-gray-100 rounded-full hover:bg-gray-100 hover:border-main bg-transparent"
                    icon={
                      <EditIcon
                        fontSize="small"
                        style={{ color: 'var(--main)' }}
                      />
                    }
                  />
                </div>
                <div
                  className="text-mainText text-small truncate max-w-[30rem] lg:max-w-[50rem] overflow-hidden whitespace-nowrap cursor-pointer"
                  onClick={() => {
                    setSelectedItemIndex(index);
                    setSelectedChunkId(item.id || null);
                  }}
                >
                  {item.content}
                </div>
              </div>
              <div className="flex items-center gap-x-2 shrink-0 pr-4">
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedItemIndex(index);
                    setSelectedChunkId(item.id || null);
                  }}
                >
                  <KeyboardArrowRightIcon fontSize="large" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (selectedItemIndex === null || !contentData[selectedItemIndex])
      return null;

    const item = contentData[selectedItemIndex];

    return (
      <div className="lg:w-2/5 flex flex-col rounded-3xl mt-6 lg:mt-0 lg:ml-4 max-h-[calc(100vh-12rem)]">
        <h3 className="text-secondary font-semibold text-medium ml-6 pb-2">
          Preview
        </h3>

        <div className="flex flex-col bg-select rounded-3xl overflow-hidden h-full p-6">
          <div className="flex-shrink-0">
            {item.similarity && (
              <div className="mb-4">
                <h3 className="text-secondary font-semibold text-medium mb-2">
                  Score
                </h3>
                <div className="text-small bg-gray-50 p-3 text-mainText rounded-2xl">
                  {item.similarity?.toFixed(5)}
                </div>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-secondary font-semibold text-medium mb-2">
                Metadata
              </h3>
              <pre className="text-small bg-gray-50 p-3 text-mainText rounded-2xl overflow-auto">
                {JSON.stringify(
                  {
                    active: item.metadata.active,
                    chunk_length: item.metadata.chunk_length,
                    chunk_idx: item.metadata.chunk_idx,
                    data_source_id: item.metadata.data_source_id,
                    sourceURL: item.metadata.sourceURL || 'N/A',
                    file_size: item.metadata.file_size || 'N/A',
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 flex-col">
            <h3 className="text-secondary font-semibold text-small mb-2">
              Text
            </h3>
            <div className="flex-1 overflow-y-auto text-mainText text-small bg-white p-4 rounded-xl">
              {item.content ? <Markdown>{item.content}</Markdown> : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSearchFields = () => {
    return (
      <div className="p-6 border-b border-border z-20 pb-4 mb-6">
        <Container className="min-h-[10rem]">
          <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-6 lg:gap-y-4 ">
            <div className="w-full lg:flex-1 xl:min-h-[10rem] lg:min-h-unset items-center">
              <FormItem<FormProps>
                name="search"
                label="Search"
                inline={false}
                required
              >
                <Input
                  placeholder="Search"
                  inputClassName="pl-20 text-medium"
                  prefix={<SearchIcon />}
                />
              </FormItem>
            </div>

            <div className="flex sm:flex-row lg:gap-4 w-full lg:w-auto flex-1 ">
              <div className="w-full sm:w-1/2 xl:min-h-[10rem] lg:min-h-unset items-center">
                <FormItem<FormProps>
                  name="limit"
                  label="Chunks"
                  inline={false}
                  required
                  validate={validateLimitSize}
                >
                  <Input placeholder="Limit" />
                </FormItem>
              </div>

              <div className="w-full sm:w-1/2 pl-4">
                <FormItem<FormProps>
                  name="threshold"
                  label="Threshold"
                  inline={false}
                  required
                  validate={validateThresholdSize}
                >
                  <Input placeholder="Threshold" />
                </FormItem>
              </div>
            </div>

            <div className="w-full flex flex-col sm:w-auto xl:min-h-[10rem] lg:min-h-unset justify-center">
              <SubmitButton
                size="large"
                label="Search"
                loading={searchingKnowledge}
                disabled={!form.search}
                className="h-[40px] w-full sm:w-auto text-medium"
                onClick={() => {
                  handleSearchKnowledge();
                  setSelectedItemIndex(null);
                  setSelectedChunkId(null);
                }}
                spinnerColor="background"
              />
            </div>
          </div>
        </Container>
      </div>
    );
  };

  return (
    <div className="sx:h-[100dvh] lg:h-full flex flex-col overflow-hidden min-h-[80vh]">
      <Form initialValue={form} onChange={setForm}>
        {isSeachModalType && renderSearchFields()}
        <div className="flex-1 flex flex-col gap-6 lg:flex-row p-6 lg:overflow-hidden ">
          <div
            className={clsx(
              'lg:w-2/3 pr-0 lg:pr-4 overflow-y-auto',
              selectedItemIndex !== null ? 'w-full' : 'flex-1'
            )}
          >
            {searchingKnowledge || loadingChunks ? (
              <Loading height={'100%'} />
            ) : (
              renderContentList()
            )}
          </div>
          {renderDetailPanel()}
        </div>
      </Form>
      {!isSeachModalType && (
        <div className="mt-sm flex justify-start items-center">
          <SearchPanel
            totalItems={pagination?.totalItems}
            page={pagination?.page}
            perPage={10}
            withPaginate
            setPage={page => {
              setSelectedItemIndex(null);
              setSelectedChunkId(null);
              setPageDetails(page);
            }}
          />
        </div>
      )}
    </div>
  );
};
